import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogOut, Trash2, CalendarDays, ClipboardList, Mail, Images, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { api, fmtUSD } from "@/lib/api";
import PhotosTab from "@/components/admin/PhotosTab";

export default function AdminDashboardPage() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("bookings");
    const [me, setMe] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [blocked, setBlocked] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [packages, setPackages] = useState([]);
    const [subscribers, setSubscribers] = useState([]);

    const [newBlockDate, setNewBlockDate] = useState(undefined);
    const [newBlockReason, setNewBlockReason] = useState("");

    useEffect(() => {
        if (!localStorage.getItem("cbc_admin_token")) {
            navigate("/admin/login");
            return;
        }
        api.get("/auth/me")
            .then((r) => setMe(r.data))
            .catch(() => {
                localStorage.removeItem("cbc_admin_token");
                navigate("/admin/login");
            });
        api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    }, [navigate]);

    const loadAll = useCallback(async () => {
        try {
            const [b, bd, c, n] = await Promise.all([
                api.get("/admin/bookings"),
                api.get("/admin/blocked-dates"),
                api.get("/admin/contacts"),
                api.get("/admin/newsletter"),
            ]);
            setBookings(b.data);
            setBlocked(bd.data);
            setContacts(c.data);
            setSubscribers(n.data);
        } catch (e) {
            if (e.response?.status === 401) {
                localStorage.removeItem("cbc_admin_token");
                navigate("/admin/login");
            }
        }
    }, [navigate]);

    useEffect(() => {
        if (me) loadAll();
    }, [me, loadAll]);

    const logout = () => {
        localStorage.removeItem("cbc_admin_token");
        navigate("/admin/login");
    };

    const updateBookingStatus = async (id, status) => {
        try {
            await api.patch(`/admin/bookings/${id}`, { status });
            toast.success("Booking updated.");
            loadAll();
        } catch {
            toast.error("Could not update.");
        }
    };

    const addBlockedDate = async () => {
        if (!newBlockDate) {
            toast.error("Pick a date first.");
            return;
        }
        try {
            await api.post("/admin/blocked-dates", {
                event_date: format(newBlockDate, "yyyy-MM-dd"),
                reason: newBlockReason || "Blocked",
            });
            toast.success("Date blocked.");
            setNewBlockDate(undefined);
            setNewBlockReason("");
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not block date.");
        }
    };

    const removeBlocked = async (event_date) => {
        try {
            await api.delete(`/admin/blocked-dates/${event_date}`);
            toast.success("Date unblocked.");
            loadAll();
        } catch {
            toast.error("Could not unblock.");
        }
    };

    const stats = useMemo(
        () => ({
            confirmed: bookings.filter((b) => b.status === "confirmed").length,
            pending: bookings.filter((b) => b.status === "pending_payment").length,
            deposits: bookings
                .filter((b) => b.status === "confirmed")
                .reduce((s, b) => s + (b.quote?.deposit_due || 0), 0),
            subscribers: subscribers.length,
        }),
        [bookings, subscribers],
    );

    if (!me) return null;

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-sand-100">
            <header className="border-b border-palm-400/30 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <div>
                        <div className="overline">Coral Bay · Admin</div>
                        <h1 className="font-serif text-2xl text-forest-700 mt-1">
                            Welcome back, Erin
                        </h1>
                    </div>
                    <button
                        onClick={logout}
                        data-testid="logout-btn"
                        className="inline-flex items-center gap-2 text-forest-700/70 hover:text-forest-700 text-sm"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
                    </button>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Stat label="Confirmed bookings" value={stats.confirmed} />
                    <Stat label="Pending payment" value={stats.pending} />
                    <Stat label="Deposits collected" value={fmtUSD(stats.deposits)} />
                    <Stat label="Subscribers" value={stats.subscribers} />
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 border-b border-palm-400/30 overflow-x-auto">
                    {[
                        { k: "bookings", label: "Bookings", icon: ClipboardList },
                        { k: "calendar", label: "Calendar", icon: CalendarDays },
                        { k: "photos", label: "Photos", icon: Images },
                        { k: "contacts", label: "Inquiries", icon: Mail },
                        { k: "subscribers", label: "Subscribers", icon: Sparkles },
                    ].map((t) => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            data-testid={`tab-${t.k}`}
                            className={`px-5 py-3 text-sm tracking-wide inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                                tab === t.k
                                    ? "text-forest-700 border-forest-700"
                                    : "text-forest-700/60 border-transparent hover:text-forest-700"
                            }`}
                        >
                            <t.icon className="w-4 h-4" strokeWidth={1.5} />
                            {t.label}
                        </button>
                    ))}
                </div>

                {tab === "bookings" && (
                    <BookingsTab
                        bookings={bookings}
                        onStatusChange={updateBookingStatus}
                    />
                )}

                {tab === "calendar" && (
                    <div
                        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
                        data-testid="calendar-tab"
                    >
                        <div className="bg-white border border-palm-400/30 rounded-sm p-6">
                            <h3 className="font-serif text-2xl text-forest-700 mb-1">
                                Block a date
                            </h3>
                            <p className="text-sm text-forest-700/60 mb-4">
                                Mark a date as unavailable on the public calendar.
                            </p>
                            <div className="bg-sand-50 border border-palm-400/20 rounded-sm p-2 inline-block">
                                <Calendar
                                    mode="single"
                                    selected={newBlockDate}
                                    onSelect={setNewBlockDate}
                                    data-testid="admin-block-date-picker"
                                />
                            </div>
                            <div className="mt-4 space-y-3">
                                <div>
                                    <Label className="text-xs tracking-[0.18em] uppercase text-forest-400">
                                        Reason (optional)
                                    </Label>
                                    <Input
                                        value={newBlockReason}
                                        onChange={(e) => setNewBlockReason(e.target.value)}
                                        placeholder="e.g. Personal day"
                                        data-testid="admin-block-reason"
                                        className="mt-2 border-palm-400/50"
                                    />
                                </div>
                                <button
                                    onClick={addBlockedDate}
                                    data-testid="admin-block-add-btn"
                                    className="bg-forest-700 text-sand-100 px-5 py-2.5 rounded-sm hover:bg-forest-900 text-sm tracking-wide"
                                >
                                    Block this date
                                </button>
                            </div>
                        </div>
                        <div className="bg-white border border-palm-400/30 rounded-sm p-6">
                            <h3 className="font-serif text-2xl text-forest-700 mb-4">
                                Blocked dates
                            </h3>
                            {blocked.length === 0 ? (
                                <p className="text-sm text-forest-700/60">
                                    No dates blocked yet.
                                </p>
                            ) : (
                                <ul className="divide-y divide-palm-400/20">
                                    {blocked.map((b) => (
                                        <li
                                            key={b.event_date}
                                            className="py-3 flex items-center justify-between"
                                            data-testid={`blocked-${b.event_date}`}
                                        >
                                            <div>
                                                <div className="font-serif text-lg text-forest-700">
                                                    {format(
                                                        new Date(b.event_date + "T00:00:00"),
                                                        "EEE, MMM d, yyyy",
                                                    )}
                                                </div>
                                                <div className="text-xs text-forest-700/60">
                                                    {b.reason} {b.auto && "· auto"}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeBlocked(b.event_date)}
                                                data-testid={`unblock-${b.event_date}`}
                                                className="text-forest-700/60 hover:text-red-700"
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {tab === "photos" && <PhotosTab packages={packages} />}

                {tab === "subscribers" && (
                    <div
                        className="bg-white border border-palm-400/30 rounded-sm"
                        data-testid="subscribers-tab"
                    >
                        <div className="p-6 border-b border-palm-400/20 flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <h3 className="font-serif text-2xl text-forest-700">
                                    Newsletter subscribers
                                </h3>
                                <p className="text-sm text-forest-700/60 mt-1">
                                    {subscribers.length} email{subscribers.length === 1 ? "" : "s"} captured.
                                    Sync to Substack on a schedule.
                                </p>
                            </div>
                            {subscribers.length > 0 && (
                                <button
                                    onClick={() => {
                                        const blob = new Blob(
                                            [
                                                "email,source,created_at\n" +
                                                    subscribers
                                                        .map(
                                                            (s) =>
                                                                `${s.email},${s.source || ""},${s.created_at}`,
                                                        )
                                                        .join("\n"),
                                            ],
                                            { type: "text/csv" },
                                        );
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = "coral-bay-subscribers.csv";
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    data-testid="export-subscribers-btn"
                                    className="text-xs tracking-wide bg-forest-700 text-sand-100 px-4 py-2 rounded-sm hover:bg-forest-900"
                                >
                                    Export CSV
                                </button>
                            )}
                        </div>
                        {subscribers.length === 0 ? (
                            <p className="p-8 text-sm text-forest-700/60">
                                No subscribers yet.
                            </p>
                        ) : (
                            <ul className="divide-y divide-palm-400/20">
                                {subscribers.map((s) => (
                                    <li
                                        key={s.id}
                                        className="px-6 py-4 flex items-baseline justify-between"
                                        data-testid={`subscriber-${s.id}`}
                                    >
                                        <div>
                                            <div className="text-forest-700">{s.email}</div>
                                            <div className="text-xs text-forest-700/60 mt-0.5">
                                                {s.source || "homepage"}
                                            </div>
                                        </div>
                                        <span className="text-xs text-forest-700/50">
                                            {new Date(s.created_at).toLocaleDateString()}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {tab === "contacts" && (
                    <div
                        className="bg-white border border-palm-400/30 rounded-sm"
                        data-testid="contacts-tab"
                    >
                        {contacts.length === 0 ? (
                            <p className="p-8 text-sm text-forest-700/60">
                                No inquiries yet.
                            </p>
                        ) : (
                            <ul className="divide-y divide-palm-400/20">
                                {contacts.map((c) => (
                                    <li
                                        key={c.id}
                                        className="p-6"
                                        data-testid={`contact-row-${c.id}`}
                                    >
                                        <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                            <div className="font-serif text-xl text-forest-700">
                                                {c.name}
                                                <span className="ml-3 text-sm text-forest-700/60">
                                                    {c.email}
                                                    {c.phone ? ` · ${c.phone}` : ""}
                                                </span>
                                            </div>
                                            <span className="text-xs text-forest-700/50">
                                                {new Date(c.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        {c.subject && (
                                            <div className="text-sm text-forest-700 mt-2 font-medium">
                                                {c.subject}
                                            </div>
                                        )}
                                        {c.event_location && (
                                            <div className="text-xs text-forest-700/60 mt-1">
                                                Location: {c.event_location}
                                            </div>
                                        )}
                                        <p className="text-sm text-forest-700/80 mt-3 leading-relaxed whitespace-pre-line">
                                            {c.message}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-white border border-palm-400/30 rounded-sm p-5">
            <div className="text-xs uppercase tracking-[0.18em] text-forest-400">
                {label}
            </div>
            <div className="font-serif text-3xl text-forest-700 mt-2">{value}</div>
        </div>
    );
}

function BookingsTab({ bookings, onStatusChange }) {
    if (bookings.length === 0) {
        return (
            <div className="bg-white border border-palm-400/30 rounded-sm p-8 text-sm text-forest-700/60">
                No bookings yet. New reservations will appear here.
            </div>
        );
    }
    return (
        <div className="bg-white border border-palm-400/30 rounded-sm overflow-x-auto" data-testid="bookings-table">
            <table className="w-full text-sm">
                <thead className="text-left border-b border-palm-400/30">
                    <tr className="text-xs uppercase tracking-[0.18em] text-forest-400">
                        <th className="p-4">Date</th>
                        <th className="p-4">Customer</th>
                        <th className="p-4">Package</th>
                        <th className="p-4">Guests</th>
                        <th className="p-4">Total</th>
                        <th className="p-4">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-palm-400/15">
                    {bookings.map((b) => (
                        <tr
                            key={b.id}
                            className="text-forest-700"
                            data-testid={`booking-row-${b.id}`}
                        >
                            <td className="p-4 align-top">
                                <div className="font-serif text-lg">{b.event_date}</div>
                                {b.event_time && (
                                    <div className="text-xs text-forest-700/60">
                                        {b.event_time}
                                    </div>
                                )}
                            </td>
                            <td className="p-4 align-top">
                                <div>{b.customer_name}</div>
                                <div className="text-xs text-forest-700/60">
                                    {b.customer_email}
                                </div>
                                <div className="text-xs text-forest-700/60">
                                    {b.customer_phone}
                                </div>
                                <div className="text-xs text-forest-700/60 max-w-xs">
                                    {b.event_location}
                                </div>
                            </td>
                            <td className="p-4 align-top">
                                <div>{b.package_name}</div>
                                {b.meal_choice && (
                                    <div className="text-xs text-forest-400 mt-1">
                                        🌀 {b.meal_choice}
                                    </div>
                                )}
                            </td>
                            <td className="p-4 align-top">{b.guests}</td>
                            <td className="p-4 align-top">
                                <div>{fmtUSD(b.quote?.total || 0)}</div>
                                <div className="text-xs text-forest-700/60">
                                    Deposit {fmtUSD(b.quote?.deposit_due || 0)}
                                </div>
                            </td>
                            <td className="p-4 align-top w-44">
                                <Select
                                    value={b.status}
                                    onValueChange={(v) => onStatusChange(b.id, v)}
                                >
                                    <SelectTrigger
                                        data-testid={`status-select-${b.id}`}
                                        className="border-palm-400/50 text-xs"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending_payment">
                                            Pending payment
                                        </SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                </Select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
